import React, { useState, useEffect } from "react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { updateCandidateProfile } from "../graphql/mutations";
import { createScreeningInterviewSlot } from "../utils/screeningUtils";
import { sendScreeningScheduleEmail } from "../utils/emailService";
import axios from "axios";
import { FiUpload, FiX, FiFileText, FiCheck, FiAlertCircle, FiFile, FiExternalLink, FiMail, FiPlay, FiSend } from "react-icons/fi";

/**
 * ScreeningRoundModal Component
 * Compact, dark-themed modal for screening interviews
 */
const ScreeningRoundModal = ({ 
  isOpen, 
  onClose, 
  candidateProfile, 
  candidateEmail,
  onScreeningCreated 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCreatingScreening, setIsCreatingScreening] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('choose'); // 'choose', 'upload', 'processing', 'complete', 'emailSent'
  const [profileData, setProfileData] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [createdSlotId, setCreatedSlotId] = useState(null);

  const RESUME_PARSER_URL = "https://phone.getscreened.in/parser/upload_resume";
  const S3_BUCKET_NAME = "getscreenedstoragebucketf24ed-devget";

  const hasExistingResume = candidateProfile?.resumeURL && 
    candidateProfile.resumeURL.length > 0 && 
    !candidateProfile.resumeURL.includes('staging');

  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setIsUploading(false);
      setIsCreatingScreening(false);
      setError(null);
      setStep('choose');
      setProfileData(null);
      setIsSendingEmail(false);
      setCreatedSlotId(null);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf' && file.size <= 10 * 1024 * 1024) {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid PDF (max 10MB)');
    }
  };

  const handleUseExisting = async () => {
    setStep('processing');
    setProfileData(candidateProfile);
    await createScreening(candidateProfile);
  };

  const handleUploadAndStart = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    setStep('processing');
    setError(null);

    try {
      // Upload to S3
      const { key } = await Storage.put(`${Date.now()}_${selectedFile.name}`, selectedFile, { contentType: "application/pdf" });
      const resumeURL = `https://${S3_BUCKET_NAME}.s3.amazonaws.com/public/${key}`;

      // Parse resume
      const formData = new FormData();
      formData.append("resume", selectedFile);
      const { data: parsed } = await axios.post(RESUME_PARSER_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // Update profile
      const updatedProfile = {
        id: candidateProfile.id,
        resumeURL,
        fullName: parsed.name || candidateProfile.fullName,
        keySkills: parsed.keySkills || candidateProfile.keySkills,
        mobileNumber: parsed.phonenumber || candidateProfile.mobileNumber,
        description: parsed.description || candidateProfile.description,
        currentCity: parsed.currentCity || candidateProfile.currentCity,
        roleApplyFor: (parsed.roleappliedfor && parsed.roleappliedfor !== "Not specified") ? parsed.roleappliedfor : candidateProfile.roleApplyFor,
        workStatus: (parsed.workstatus && parsed.workstatus !== "Not specified") ? parsed.workstatus : candidateProfile.workStatus,
      };

      await API.graphql(graphqlOperation(updateCandidateProfile, { input: updatedProfile }));
      setProfileData({ ...updatedProfile, email: candidateEmail });
      await createScreening({ ...updatedProfile, email: candidateEmail });

    } catch (err) {
      console.error('Error:', err);
      setError('Failed to process resume. Try again.');
      setStep('upload');
    } finally {
      setIsUploading(false);
    }
  };

  const createScreening = async (profile, startImmediately = true) => {
    setIsCreatingScreening(true);
    try {
      const slot = await createScreeningInterviewSlot({
        candidateProfileID: candidateProfile.id,
        candidateEmail,
        candidateProfile: profile,
        numberOfQuestions: 5
      });

      localStorage.setItem('isScreeningRound', 'true');
      localStorage.setItem('screeningSlotId', slot.id);
      localStorage.setItem('candidateEmail', candidateEmail);
      
      setCreatedSlotId(slot.id);

      if (startImmediately) {
        setStep('complete');
        setTimeout(() => onScreeningCreated?.(slot), 1200);
      }
      
      return slot;

    } catch (err) {
      console.error('Error creating screening:', err);
      setError('Failed to create interview. Try again.');
      setStep('choose');
      return null;
    } finally {
      setIsCreatingScreening(false);
    }
  };

  // Handle sending interview link via email
  const handleSendEmailLink = async (profile) => {
    setIsSendingEmail(true);
    setStep('processing');
    try {
      // First create the screening slot
      const slot = await createScreening(profile, false);
      
      if (!slot) {
        throw new Error('Failed to create screening slot');
      }

      // Generate the interview link
      const interviewLink = `${window.location.origin}/public-interview/${slot.id}`;

      // Send email with the interview link
      await sendScreeningScheduleEmail({
        candidateEmail,
        candidateName: profile?.fullName || candidateProfile?.fullName || 'Candidate',
        interviewLink,
        slotId: slot.id,
      });

      setStep('emailSent');
      console.log('Interview link email sent successfully');
    } catch (err) {
      console.error('Failed to send email:', err);
      setError('Failed to send email. Try again.');
      setStep('choose');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-lg w-full max-w-sm border border-slate-700 shadow-xl">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-white font-medium">Screening Round</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <FiX size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Choose Step */}
          {step === 'choose' && (
            <div className="space-y-3">
              {hasExistingResume && (
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  <div className="p-3 bg-slate-800/30">
                    <div className="flex items-center gap-3">
                      <FiFile className="text-slate-400" size={18} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">Current Resume</p>
                        <p className="text-slate-500 text-xs mt-0.5 truncate">{candidateProfile?.roleApplyFor || 'Your existing profile'}</p>
                      </div>
                      <a
                        href={candidateProfile.resumeURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-white text-xs flex items-center gap-1"
                      >
                        <FiExternalLink size={12} />
                      </a>
                    </div>
                  </div>
                  <div className="border-t border-slate-700 p-2 flex gap-2">
                    <button
                      onClick={handleUseExisting}
                      className="flex-1 py-2 px-3 text-xs rounded bg-white text-slate-900 hover:bg-slate-200 transition-all flex items-center justify-center gap-1.5"
                    >
                      <FiPlay size={12} />
                      Start Now
                    </button>
                    <button
                      onClick={() => handleSendEmailLink(candidateProfile)}
                      disabled={isSendingEmail}
                      className="flex-1 py-2 px-3 text-xs rounded bg-slate-700 text-white hover:bg-slate-600 transition-all flex items-center justify-center gap-1.5"
                    >
                      <FiMail size={12} />
                      Email Link
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => setStep('upload')}
                className="w-full text-left p-3 rounded-lg border border-slate-700 hover:border-slate-500 hover:bg-slate-800/50 transition-all"
              >
                <div className="flex items-center gap-3">
                  <FiUpload className="text-slate-400" size={18} />
                  <div>
                    <p className="text-white text-sm font-medium">Upload new resume</p>
                    <p className="text-slate-500 text-xs mt-0.5">Update profile before screening</p>
                  </div>
                </div>
              </button>

              <p className="text-slate-500 text-xs text-center pt-2">
                AI will ask questions based on your resume
              </p>
            </div>
          )}

          {/* Upload Step */}
          {step === 'upload' && (
            <div className="space-y-3">
              <div className={`border border-dashed rounded-lg p-4 text-center ${selectedFile ? 'border-slate-500' : 'border-slate-700'}`}>
                {selectedFile ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiFileText className="text-slate-400" size={16} />
                      <span className="text-white text-sm truncate max-w-[180px]">{selectedFile.name}</span>
                    </div>
                    <button onClick={() => setSelectedFile(null)} className="text-slate-400 hover:text-white">
                      <FiX size={16} />
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <FiUpload className="text-slate-500 mx-auto mb-2" size={20} />
                    <p className="text-slate-400 text-sm">Click to upload PDF</p>
                    <input type="file" accept=".pdf" onChange={handleFileChange} className="hidden" />
                  </label>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-xs">
                  <FiAlertCircle size={14} />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => setStep('choose')}
                  className="flex-1 py-2 text-sm rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 transition-all"
                >
                  Back
                </button>
                <button
                  onClick={handleUploadAndStart}
                  disabled={!selectedFile || isUploading}
                  className={`flex-1 py-2 text-sm rounded-lg transition-all ${
                    selectedFile && !isUploading
                      ? 'bg-white text-slate-900 hover:bg-slate-200'
                      : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  {isUploading ? 'Processing...' : 'Start'}
                </button>
              </div>
            </div>
          )}

          {/* Processing Step */}
          {step === 'processing' && (
            <div className="py-6 text-center">
              <div className="w-8 h-8 border-2 border-slate-600 border-t-white rounded-full animate-spin mx-auto mb-3" />
              <p className="text-slate-300 text-sm">Setting up interview...</p>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && (
            <div className="py-6 text-center">
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-3">
                <FiCheck className="text-white" size={20} />
              </div>
              <p className="text-white text-sm font-medium">Ready!</p>
              <p className="text-slate-500 text-xs mt-1">Redirecting...</p>
            </div>
          )}

          {/* Email Sent Step */}
          {step === 'emailSent' && (
            <div className="py-4 text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                <FiMail className="text-emerald-400" size={22} />
              </div>
              <p className="text-white text-sm font-medium mb-1">Email Sent!</p>
              <p className="text-slate-400 text-xs mb-4 px-2">
                Interview link sent to<br />
                <span className="text-slate-300">{candidateEmail}</span>
              </p>
              <p className="text-slate-500 text-xs mb-4">
                Check your inbox and start the interview anytime - it's available 24/7!
              </p>
              <div className="flex gap-2">
                {createdSlotId && (
                  <button
                    onClick={() => {
                      onScreeningCreated?.({ id: createdSlotId });
                    }}
                    className="flex-1 py-2 text-xs rounded-lg bg-slate-700 text-white hover:bg-slate-600 transition-all flex items-center justify-center gap-1.5"
                  >
                    <FiPlay size={12} />
                    Start Now Instead
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="flex-1 py-2 text-xs rounded-lg bg-white text-slate-900 hover:bg-slate-200 transition-all"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreeningRoundModal;
